import {
    Card, CardContent
} from 'shared/components/ui/card';
import {
    Button
} from 'shared/components/ui/button';
import React, {
    useContext, useEffect, useState
} from 'react';
import {
    useNavigate, useParams
} from 'react-router-dom';
import {
    Context
} from 'shared/context/GlobalState';
import {
    channelService, baileysBridgeService
} from 'shared/api';
import AlertModal from 'shared/components/Alertmodal';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from 'shared/components/ui/dropdown-menu';
import {
    formatDateToDDMMYYY
} from 'shared/utils/timeUtils';
import {
    getOrganizationMember
} from 'shared/utils/organizationUtils';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    WebChannelUpdateDialog
} from './WebChatUpdate';
import {
    CalendarDays, Cog, Mail, MessageCircleMore, MoreVertical, Phone, Trash2, User, Zap
} from 'lucide-react';
import {
    FaWhatsapp, FaFacebookMessenger
} from 'react-icons/fa';
import WhatsAppUpdate from './WhatsApp/WhatsAppUpdate';
import VoiceUpdate from './Voice/VoiceUpdate';
import MessengerUpdate from './Messenger/MessengerUpdate';
import {
    ChannelTypeUpdateDialog
} from './ChannelType';
import {
    Channel, RedisChannelDelete
} from '../types/builderChannel.types';
import { cn } from 'shared/lib/utils';

interface ChannelCardProps {
    channel: Channel;
    setEmailChannels: React.Dispatch<React.SetStateAction<Channel[]>>;
}

interface ChannelMeta {
    icon: React.ReactNode;
    iconXl: React.ReactNode;
    color: string;
    label: string;
}

const CHANNEL_META: Record<string, ChannelMeta> = {
    Email: {
        icon: <Mail size={13} />,
        iconXl: <Mail size={72} />,
        color: '#3b82f6',
        label: 'Email',
    },
    Webchat: {
        icon: <MessageCircleMore size={13} />,
        iconXl: <MessageCircleMore size={72} />,
        color: '#10b981',
        label: 'Web Chat',
    },
    'Web Chat': {
        icon: <MessageCircleMore size={13} />,
        iconXl: <MessageCircleMore size={72} />,
        color: '#10b981',
        label: 'Web Chat',
    },
    WhatsApp: {
        icon: <FaWhatsapp size={13} />,
        iconXl: <FaWhatsapp size={72} />,
        color: '#25D366',
        label: 'WhatsApp',
    },
    Whatsapp: {
        icon: <FaWhatsapp size={13} />,
        iconXl: <FaWhatsapp size={72} />,
        color: '#25D366',
        label: 'WhatsApp',
    },
    Messenger: {
        icon: <FaFacebookMessenger size={13} />,
        iconXl: <FaFacebookMessenger size={72} />,
        color: '#0084FF',
        label: 'Messenger',
    },
    Voice: {
        icon: <Phone size={13} />,
        iconXl: <Phone size={72} />,
        color: '#f59e0b',
        label: 'Voice',
    },
    // Lowercase keys (S-0005: server stores lowercase `type`)
    email: {
        icon: <Mail size={13} />,
        iconXl: <Mail size={72} />,
        color: '#3b82f6',
        label: 'Email',
    },
    webchat: {
        icon: <MessageCircleMore size={13} />,
        iconXl: <MessageCircleMore size={72} />,
        color: '#10b981',
        label: 'Web Chat',
    },
    whatsapp: {
        icon: <FaWhatsapp size={13} />,
        iconXl: <FaWhatsapp size={72} />,
        color: '#25D366',
        label: 'WhatsApp',
    },
    messenger: {
        icon: <FaFacebookMessenger size={13} />,
        iconXl: <FaFacebookMessenger size={72} />,
        color: '#0084FF',
        label: 'Messenger',
    },
    voice: {
        icon: <Phone size={13} />,
        iconXl: <Phone size={72} />,
        color: '#f59e0b',
        label: 'Voice',
    },
};

const DEFAULT_META: ChannelMeta = {
    icon: <MessageCircleMore size={13} />,
    iconXl: <MessageCircleMore size={72} />,
    color: '#6b7280',
    label: 'Channel',
};

export default function ChannelCard({
    channel, setEmailChannels
}: ChannelCardProps) {

    const {
        _id, topic, type, created, createdBy, send_mode, receive_enabled
    } = channel;

    // Server now stores lowercase `type`; keep title-case lookups working too.
    const typeKey = (type || '').toLowerCase();
    const meta = CHANNEL_META[type] ?? CHANNEL_META[typeKey] ?? { ...DEFAULT_META, label: type };

    const [emailUpdateObject, setEmailUpdateObject] = useState<Channel>(channel);
    const [delBtn, setDelBtn] = useState(false);
    const [delModal, setDelModal] = useState(false);
    const [emailUpdateDialog, setEmailUpdateDialog] = useState(false);
    const [webChatUpdateDialog, setWebChatUpdateDialog] = useState(false);
    const [whatsAppUpdateDialog, setWhatsAppUpdateDialog] = useState(false);
    const [voiceUpdateDialog, setVoiceUpdateDialog] = useState(false);
    const [updateMessenger, setUpdateMessenger] = useState(false);
    const [createdByName, setCreatedByName] = useState('');

    const { oragID, subOragID } = useParams<{ oragID: string; subOragID: string }>();
    const { addNewNotifcation } = useContext(Context);
    const { selectedOrganization } = useContext(OrganizationContext);
    const navigate = useNavigate();

    useEffect(() => {
        getOrganizationMember(createdBy, selectedOrganization)
            .then((name: string) => setCreatedByName(name))
            .catch(() => {});
    }, [createdBy, selectedOrganization]);

    const handleDeleteEmail = () => {
        setDelBtn(true);
        const deleteChannelObj: RedisChannelDelete = {
            org_id: oragID || '',
            sub_org_id: subOragID || '',
            channelId: _id
        };
        if (typeKey === 'whatsapp') {
            baileysBridgeService.disconnect(oragID || '', subOragID || '', channel.address || '').catch(() => {});
        }
        channelService.deleteChannel(deleteChannelObj)
            .then(() => {
                setEmailChannels(prev => prev.filter(item => item._id !== _id));
                addNewNotifcation('Channel deleted successfully', 'success');
                setDelModal(false);
            }).catch(() => {
                addNewNotifcation('Something went wrong', 'danger');
            }).finally(() => setDelBtn(false));
    };

    const handleChannelUpdate = () => {
        switch (typeKey) {
            case 'email': setEmailUpdateDialog(true); break;
            case 'web chat':
            case 'webchat': setWebChatUpdateDialog(true); break;
            case 'whatsapp': setWhatsAppUpdateDialog(true); break;
            case 'messenger': setUpdateMessenger(true); break;
            case 'voice': setVoiceUpdateDialog(true); break;
            default: setEmailUpdateDialog(true); break;
        }
    };

    const stop = (e: React.MouseEvent, cb?: () => void) => { e.stopPropagation(); cb?.(); };
    const isWhatsApp = typeKey === 'whatsapp';

    return (
        <>
            <div
                className="group cursor-pointer relative"
                onClick={() => navigate(`${_id}`, { state: { type, topic } })}
            >
                {/* Card shell */}
                <div className="relative rounded-xl border border-border bg-card overflow-hidden transition-all duration-250 group-hover:shadow-[0_4px_24px_-4px_rgba(0,0,0,0.12)] group-hover:-translate-y-px">

                    {/* Left accent bar */}
                    <div
                        className="absolute inset-y-0 left-0 w-[3px] group-hover:w-[5px] transition-[width] duration-300 rounded-l-xl"
                        style={{ backgroundColor: meta.color }}
                    />

                    {/* Watermark icon */}
                    <div
                        className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none select-none"
                        style={{ color: meta.color, opacity: 0.07 }}
                    >
                        {meta.iconXl}
                    </div>

                    <div className="pl-5 pr-4 pt-4 pb-4 flex flex-col gap-3 relative">

                        {/* Top row: type chip + badges + menu */}
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                {/* Type chip */}
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-[3px] rounded text-[10px] font-bold uppercase tracking-widest"
                                    style={{
                                        backgroundColor: `${meta.color}14`,
                                        color: meta.color,
                                    }}
                                >
                                    {meta.icon}
                                    {meta.label}
                                </span>
                                {/* Send/receive posture badge (S-0005: derived from send_mode/receive_enabled) */}
                                {receive_enabled === false ? (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-[3px] rounded text-[10px] font-semibold border border-amber-200 text-amber-600 dark:border-amber-800 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40">
                                        <Zap size={9} />
                                        Send only
                                    </span>
                                ) : send_mode === 'disabled' ? (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-[3px] rounded text-[10px] font-semibold border border-border text-muted-foreground bg-muted/40">
                                        No outbound
                                    </span>
                                ) : null}
                            </div>

                            {/* Menu — stop propagation */}
                            <DropdownMenu modal={false}>
                                <DropdownMenuTrigger onClick={(e) => stop(e)} asChild>
                                    <Button
                                        variant="ghost"
                                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground hover:bg-muted rounded"
                                    >
                                        <MoreVertical className="h-3.5 w-3.5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent onClick={(e) => stop(e)} align="end">
                                    <DropdownMenuLabel className="text-xs">Actions</DropdownMenuLabel>
                                    <DropdownMenuItem onClick={handleChannelUpdate}>
                                        <Cog className="mr-2 h-3.5 w-3.5" />
                                        <span>Manage</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={() => setDelModal(true)}
                                        className="text-destructive focus:text-destructive"
                                    >
                                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                                        <span>Delete</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>

                        {/* Channel name */}
                        <div className="pr-14">
                            <h3
                                className="text-sm font-semibold text-foreground leading-snug line-clamp-2 transition-colors duration-200 group-hover:text-primary"
                            >
                                {topic || <span className="text-muted-foreground font-normal italic">Unnamed channel</span>}
                            </h3>
                        </div>

                        {/* Footer meta */}
                        <div className="flex items-center gap-3 pt-2 border-t border-border/50 text-[11px] text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                                <CalendarDays size={11} className="shrink-0" />
                                {formatDateToDDMMYYY(created)}
                            </span>
                            {createdByName && (
                                <>
                                    <span className="opacity-30">·</span>
                                    <span className="inline-flex items-center gap-1 truncate min-w-0">
                                        <User size={11} className="shrink-0" />
                                        <span className="truncate">{createdByName}</span>
                                    </span>
                                </>
                            )}
                        </div>

                    </div>
                </div>
            </div>

            <ChannelTypeUpdateDialog
                setEmailChannels={setEmailChannels}
                isOpen={emailUpdateDialog}
                setOpenEmailModal={setEmailUpdateDialog}
                channel={channel}
            />
            <WebChannelUpdateDialog
                setEmailUpdateObject={setEmailUpdateObject}
                emailUpdateObject={emailUpdateObject}
                setEmailChannels={setEmailChannels}
                openEmailModal={webChatUpdateDialog}
                setOpenEmailModal={setWebChatUpdateDialog}
            />
            <WhatsAppUpdate
                isOpen={whatsAppUpdateDialog}
                setOpenWhatsAppModal={setWhatsAppUpdateDialog}
                channel={channel}
                setEmailChannels={setEmailChannels}
            />
            <VoiceUpdate
                isOpen={voiceUpdateDialog}
                setOpenVoiceModal={setVoiceUpdateDialog}
                channel={channel}
                setEmailChannels={setEmailChannels}
            />
            <MessengerUpdate
                isOpen={updateMessenger}
                setOpenMessengerModal={setUpdateMessenger}
                channel={channel}
                setEmailChannels={setEmailChannels}
            />
            <AlertModal loading={delBtn} openModal={delModal} setopenModal={setDelModal} onDelete={handleDeleteEmail} />
        </>
    );
}
